import { useState, useEffect } from "react"
import { X } from "lucide-react"
import { useNavigate } from "react-router-dom"

interface NotificationToastProps {
  title?: string
  body?: string
  action?: string
  onDismiss: () => void
}

export function NotificationToast({ title, body, action, onDismiss }: NotificationToastProps) {
  const navigate = useNavigate()

  useEffect(() => {
    const timer = setTimeout(onDismiss, 6000)
    return () => clearTimeout(timer)
  }, [onDismiss])

  const handleClick = () => {
    if (action === "open_hotspots") navigate("/hotspots")
    else if (action === "open_events") navigate("/events")
    else if (action === "open_earnings") navigate("/")
    else if (action === "open_airport") navigate("/hotspots")
    onDismiss()
  }

  return (
    <div
      onClick={handleClick}
      className="cursor-pointer bg-card border shadow-lg rounded-lg p-4 max-w-sm w-full animate-in slide-in-from-top-2 fade-in"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          {title && <p className="font-semibold text-sm truncate">{title}</p>}
          {body && <p className="text-sm text-muted-foreground line-clamp-2">{body}</p>}
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onDismiss() }}
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}

interface ToastItem {
  id: string
  title?: string
  body?: string
  action?: string
}

export function NotificationToastContainer() {
  const [toasts, setToasts] = useState<ToastItem[]>([])

  useEffect(() => {
    const handler = (event: CustomEvent<ToastItem>) => {
      setToasts((prev) => [...prev, event.detail])
    }
    window.addEventListener("ezer:notification" as string, handler as EventListener)
    return () => window.removeEventListener("ezer:notification" as string, handler as EventListener)
  }, [])

  const dismiss = (id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }

  return (
    <div className="fixed top-4 right-4 z-[100] space-y-2">
      {toasts.map((toast) => (
        <NotificationToast
          key={toast.id}
          title={toast.title}
          body={toast.body}
          action={toast.action}
          onDismiss={() => dismiss(toast.id)}
        />
      ))}
    </div>
  )
}
